// src/contexts/AuthContext.jsx
import React, { createContext, useEffect, useState } from 'react';
import apiClient from '../services/apiClient';
import {
  getToken, setToken, removeToken,
  setDesign, removeDesign,
} from '../utils/authHelpers';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    isAuthenticated: false,
    accessToken: null,
    user: null,
    design: null,
  });

  const [loading, setLoading] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    let retries = 0;

    const hydrateSession = () => {
      // console.log("AUTH DEBUG search:", window.location.search);
      // console.log("AUTH DEBUG hash:", window.location.hash);
      // console.log("AUTH DEBUG localStorage.access_token:", localStorage.getItem("access_token"));

      let token = getToken();
      // console.log("📦 Token from localStorage:", token);

      // ✅ Fallback: try recovering token from URL if missing
      if (!token) {
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get("token") || urlParams.get("access_token");

        if (urlToken) {
          // console.log("🔁 Recovered token from URL:", urlToken.slice(0, 16) + "...");
          setToken(urlToken);
          token = urlToken;

          // Clean the URL and restart hydration
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          window.location.reload();
          return;
        }
      }

      if (!token) {
        if (retries < 5) {
          retries += 1;
          return setTimeout(hydrateSession, 150); // wait & retry
        }
        console.warn("⚠️ No token found after retrying. User is not authenticated.");
        setLoading(false);
        setSessionReady(true);
        return;
      }

      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      apiClient.get('/api/users/session')
        .then(({ data: session }) => {
          const designData = { designCode: session.design_code };
          setDesign(designData);

          setAuth({
            isAuthenticated: true,
            accessToken: token,
            user: session,
            design: designData,
          });

          // console.log("✅ AuthContext initialized:", session);
        })
        .catch(err => {
          // 🚨 Detailed Axios error logging:
          const base = err.config?.baseURL ?? '';
          const path = err.config?.url ?? '';
          console.error('👉 Full request URL:', `${base}${path}`);
          console.error('👉 Error code:', err.code);
          console.error('👉 Error message:', err.message);
          console.error('👉 Response status:', err.response?.status);
          console.error('👉 err.toJSON():', err.toJSON());

          // Now handle only 401 as a real logout, otherwise leave token intact
          if (err.response?.status === 401) {
            console.warn('🔒 401 Unauthorized—clearing token.');
            removeToken();
            removeDesign();
            setAuth({
              isAuthenticated: false,
              accessToken: null,
              user: null,
              design: null,
            });
          } else {
            console.warn('⚠️ Non-auth network error—keeping token to retry later.');
          }
        })
        .finally(() => {
          setLoading(false);
          setSessionReady(true);
        });
    };

    hydrateSession();
  }, []);

  const logout = () => {
    removeToken();
    removeDesign();
    setAuth({
      isAuthenticated: false,
      accessToken: null,
      user: null,
      design: null,
    });
    // console.log("🚪 User logged out.");
  };

  return (
    <AuthContext.Provider value={{ auth, logout, loading, sessionReady }}>
      {children}
    </AuthContext.Provider>
  );
};
